import { FilterType } from "./FiltersPanel";

export const STATUS_COLOR_MAP = new Map<string, string>([
  ["CUSTOMER - Closed Won",               "#D32F2F"], // Red
  ["CUSTOMER - No Response",              "#C2185B"], // Pink
  ["CUSTOMER - At Risk",                  "#512DA8"], // Deep Purple
  ["CUSTOMER - Lost (C)",                 "#00796B"], // Teal
  ["CUSTOMER - Lost",                     "#388E3C"], // Green
  ["CUSTOMER - Inactive",                 "#F57C00"], // Orange
  ["PROSPECT - Verified",                 "#4A148C"], // Darker Purple
  ["PROSPECT - Sales Ready",              "#004D40"], // Darker Teal
  ["PROSPECT - Lost",                     "#33691E"], // Darker Light‑Green
  ["PROSPECT - Sales Qualified",          "#E65100"], // Darker Orange
  ["PROSPECT - Present",                  "#FF8F00"], // Darker Amber
  ["LEAD - Unqualified",                  "#1565C0"], // Soft Blue
  ["LEAD - Sales Rep",                    "#0277BD"], // Soft Light‑Blue
  ["LEAD - New",                          "#00838F"], // Soft Cyan
  ["LEAD - Established",                  "#9E9D24"], // Soft Lime
  ["LEAD - Lost (L)",                     "#F9A825"], // Soft Yellow
  ["LEAD - LEAD-INCOMPLETE",              "#4E342E"], // Soft Brown
  ["LEAD - LEAD-READY2SUBMIT",            "#424242"], // Soft Grey
  ["LEAD - LEAD-AWAITING-NETSUITE",       "#37474F"], // Soft Blue‑Grey
  ["LEAD - No Response",                  "#17172F"], // Softer Blue‑Grey
  ["BLANK",                               "#7E7E7E"], //gray
]);

export const SPECIALTY_COLOR_MAP = new Map<string, string>([
  ["RWS",                     "#E53935"], // Red
  ["BCHN",                    "#D81B60"], // Pink
  ["BS in Exercise Kinesiology","#8E24AA"], // Purple
  ["BS Nutrition",            "#5E35B1"], // Deep Purple
  ["CCN",                     "#3949AB"], // Indigo
  ["CNC",                     "#1E88E5"], // Blue
  ["CNS",                     "#039BE5"], // Light Blue
  ["CTNC",                    "#00ACC1"], // Cyan
  ["DC",                      "#00897B"], // Teal
  ["Dentist",                 "#43A047"], // Green
  ["DO",                      "#7CB342"], // Light Green
  ["DOM",                     "#C0CA33"], // Lime
  ["DPM",                     "#1B5E20"], // Dark Green
  ["DSS-Health Coach",        "#FDD835"], // Yellow
  ["DSS-RN",                  "#FFB300"], // Amber
  ["DVM",                     "#FB8C00"], // Orange
  ["FDN",                     "#311B92"], // Dark Deep Purple
  ["International",           "#F4511E"], // Deep Orange
  ["LAc",                     "#6D4C41"], // Brown
  ["LPC/LCSW",                "#757575"], // Grey
  ["MD",                      "#546E7A"], // Blue‑Grey
  ["Midwife",                 "#EF5350"], // Soft Red
  ["MS Nutrition",            "#EC407A"], // Soft Pink
  ["NANP Professional",       "#AB47BC"], // Soft Purple
  ["ND",                      "#7E57C2"], // Soft Deep‑Purple
  ["NTP",                     "#5C6BC0"], // Soft Indigo
  ["Nurse Practitioner",     "#42A5F5"], // Soft Blue
  ["OD",                      "#29B6F6"], // Soft Light‑Blue
  ["Other",                   "#26C6DA"], // Soft Cyan
  ["PA",                      "#26A69A"], // Soft Teal
  ["Pharmacist",              "#66BB6A"], // Soft Green
  ["PhD/PsyD",                "#9CCC65"], // Soft Light‑Green
  ["PT/DPT",                  "#D4E157"], // Soft Lime
  ["RD",                      "#FFEE58"], // Soft Yellow
  ["RN",                      "#FFCA28"], // Soft Amber
  ["BLANK",                   "#7E7E7E"], //gray
]);

// EIS-1088: the four distinct Winback_practitioners.data_source values, mapped to
// the same user-facing labels the practitioner list uses (see
// helpers/practitioner.ts getDataSourcePillText). Keys are the wire values sent
// to /api/get_practitioners_for_chat_map as `data_source`.
export const DATA_SOURCE_LABEL_MAP = new Map<string, string>([
  ["PRACTITIONER_ACCOUNT_VIEW", "Direct"],
  ["PRACTITIONER_ACCOUNT_VIEW_NM", "Badger"],
  ["PRACTITIONER_DISTRIBUTOR_ACCOUNT_VIEW", "Distributor"],
  ["SALES ASSISTANT", "Sales Assistant"],
]);

// EIS-1088: "has" mirrors the practitioner list's dollar-sign predicate; "none"
// is its exact complement (verified 156 + 1,011 = 1,167 -- no overlap, no gap).
//
// EIS-1091: because they are complements, two independent on/off switches
// exposed a FOURTH state -- both off -- whose only possible meaning was "show
// nothing". That is now UNREPRESENTABLE rather than merely guarded: the filter's
// value is a single scalar with exactly three inhabitants, so there is no
// "neither" to select and no `has_previous_sales: []` for buildMapFilterBody to
// derive. Do NOT reintroduce an array shape here.
//
// EIS-1097: these three are now the DISCRIMINANT of PreviousSalesState (below),
// not the whole value -- the merged row also carries a window and, under "has",
// a dollar range. previousSalesSelection() survives unchanged as the coercion for
// the LEGACY scalar/array shapes that localStorage still holds.
export const PREVIOUS_SALES_ALL = "all";
export const PREVIOUS_SALES_HAS = "has";
export const PREVIOUS_SALES_NONE = "none";

export type PreviousSalesSelection =
  | typeof PREVIOUS_SALES_ALL
  | typeof PREVIOUS_SALES_HAS
  | typeof PREVIOUS_SALES_NONE;

/**
 * The segmented control's options, in display order. `All` is first and is the
 * default; it is the "filter off" selection, so it sends NO wire key.
 */
export const PREVIOUS_SALES_LABEL_MAP = new Map<PreviousSalesSelection, string>([
  [PREVIOUS_SALES_ALL, "All"],
  [PREVIOUS_SALES_HAS, "Has previous sales"],
  [PREVIOUS_SALES_NONE, "No previous sales"],
]);

/**
 * EIS-1091: coerce ANY value into one of the three selections. This is the single
 * migration/normalization point for the DISCRIMINANT -- withFilterDefaults,
 * buildMapFilterBody, isFilterActive and the editor itself all route through it
 * (via previousSalesState), so no code path can disagree about what a persisted
 * blob means.
 *
 * It must accept, and never crash on:
 *   - the scalar the EIS-1091 build wrote:  "all" | "has" | "none"
 *   - the EIS-1088 ARRAY:
 *       ["has","none"] -> All   (both on == filter inactive)
 *       ["has"]        -> Has
 *       ["none"]       -> No
 *       []             -> All   <-- THE TRAP. Under EIS-1088 this legitimately
 *                                  meant "show nothing". There is no such state
 *                                  any more, and resurrecting it as an empty map
 *                                  would look exactly like the bug EIS-1091
 *                                  existed to delete. It degrades to All.
 *   - a missing key, null, undefined  -> All
 *   - junk (hand-edited blob, schema drift, non-array, numbers, objects) -> All
 *
 * Degrading to All is always safe: All is the identity element of this filter --
 * it removes no pins -- so the worst case is "the user's saved narrowing was
 * forgotten", never "the map is mysteriously empty" or a white screen.
 */
export const previousSalesSelection = (value: unknown): PreviousSalesSelection => {
  if (value === PREVIOUS_SALES_HAS || value === PREVIOUS_SALES_NONE) {
    return value;
  }
  if (value === PREVIOUS_SALES_ALL) {
    return PREVIOUS_SALES_ALL;
  }
  if (Array.isArray(value)) {
    // Membership, never position: `value[0] === "has"` would be an accident of
    // how the old multi-select happened to order its values.
    const has = value.includes(PREVIOUS_SALES_HAS);
    const none = value.includes(PREVIOUS_SALES_NONE);
    if (has && !none) {
      return PREVIOUS_SALES_HAS;
    }
    if (none && !has) {
      return PREVIOUS_SALES_NONE;
    }
    // both selected, empty, or nothing recognisable -> the filter is off.
    return PREVIOUS_SALES_ALL;
  }
  return PREVIOUS_SALES_ALL;
};

// ---------------------------------------------------------------------------
// EIS-1097: the MERGED "Previous Sales" filter.
//
// EIS-1091's three-way segment and EIS-1093's standalone "Rolling 12-Month
// Sales" dollar range asked ONE question in two rows that could contradict each
// other ("no previous sales" AND "$1,000-$5,000 of them"). They are now one row
// with progressive disclosure, and the 12-month window is user-selectable:
//
//   All  (default) -> reveals nothing, filter inactive
//   Has            -> dollar range AND window
//   No             -> window only (a dollar range is meaningless with no sales)
//
// The window DEFINES both segments: Has == sales_in_window > 0 (then narrowed by
// the range), No == its exact complement in the SAME window.
// ---------------------------------------------------------------------------

/** The preset windows, in display order. Mirrors the backend's
 *  PREVIOUS_SALES_WINDOW_FIELDS map -- adding a fifth here without adding the
 *  matching rolling_Nm_sales field server-side gets an explicit 400, never a
 *  silent fallback to 12. */
export const PREVIOUS_SALES_WINDOW_OPTIONS = [3, 6, 12, 24] as const;

export type PreviousSalesWindowMonths = typeof PREVIOUS_SALES_WINDOW_OPTIONS[number];

export const DEFAULT_PREVIOUS_SALES_WINDOW_MONTHS: PreviousSalesWindowMonths = 12;

export const PREVIOUS_SALES_WINDOW_LABEL_MAP =
  new Map<PreviousSalesWindowMonths, string>([
    [3, "last 3 months"],
    [6, "last 6 months"],
    [12, "last 12 months"],
    [24, "last 24 months"],
  ]);

/**
 * Coerce anything into one of the four preset windows.
 *
 * The FRONTEND defaults junk to 12 on purpose, and that is NOT the "silently
 * default to 12" the backend is forbidden from doing. The two are different
 * questions: the server must reject a window it cannot honour (answering the
 * wrong question confidently is the failure), whereas this function's input is a
 * localStorage blob that may predate the window entirely -- and 12 is exactly
 * what every such blob meant, because 12 was the only window that existed.
 */
export const previousSalesWindowMonths = (value: unknown): PreviousSalesWindowMonths => {
  // ONLY A SCALAR MAY BE COERCED. `Number(x)` on an object calls x[Symbol.
  // toPrimitive] / x.valueOf() / x.toString(), so a blob carrying
  // `{"valueOf": null, "toString": null}` -- which JSON.parse produces verbatim,
  // so it is reachable from a real localStorage value -- makes it THROW
  // "TypeError: Cannot convert object to primitive value". This function runs
  // inside withFilterDefaults during LAYOUT RENDER, so that throw is a WHITE
  // SCREEN on the map: precisely what AC11 forbids. A Symbol throws too.
  //
  // Refusing non-scalars outright also stops schema drift from PICKING a window:
  // `Number(["24"]) === 24` would have let a boxed value silently select a
  // window the user never chose.
  const parsed = typeof value === "number"
    ? value
    : typeof value === "string" && value.trim() !== ""
      ? Number(value)
      : NaN;
  const match = PREVIOUS_SALES_WINDOW_OPTIONS
    .find((months) => months === parsed);
  return match ?? DEFAULT_PREVIOUS_SALES_WINDOW_MONTHS;
};

/**
 * Normalize whatever a NumberInput / localStorage hands back into a dollar bound.
 *
 * A cleared Mantine NumberInput emits "" (NOT null, NOT 0). Turning that into 0
 * would convert "$5,000 and up" into "$0 - $5,000" -- the exact opposite set --
 * so "" MUST become null, which buildMapFilterBody then OMITS from the body.
 * Junk (a hand-edited or schema-drifted blob) degrades to null rather than NaN.
 *
 * A real 0 stays 0: under the merged filter it is a legal, harmless minimum,
 * because the Has segment already asserts sales > 0. See the note on
 * PreviousSalesState below.
 */
export const normalizeDollarBound = (
  value: number | string | null | undefined | unknown,
): number | null => {
  // A BOUND IS A FINITE NUMBER OR A NUMERIC STRING, AND NOTHING ELSE.
  //
  // Two separate bugs live in the obvious `Number(value)`, and the silent one is
  // the worse of the pair:
  //
  //  1. SILENT. `Number([]) === 0`, `Number(true) === 1`, `Number(new Date(0))
  //     === 0`. So a drifted blob `{mode:"has", min:{}, max:[]}` migrated to
  //     "has previous sales AND at most $0" -- and Has already asserts
  //     `sales_in_window > 0`, so that asks the server for `sales > 0 AND
  //     sales <= 0`: an EMPTY MAP, with no error and no toast. AC11's "no
  //     emptied map" is exactly this.
  //  2. LOUD. `Number({valueOf: null, toString: null})` THROWS, and this runs
  //     during layout render, so it white-screens the map.
  //
  // Everything that is not a scalar therefore degrades to null = NO BOUND, which
  // buildMapFilterBody OMITS from the body. More pins, never fewer.
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string") {
    // A cleared Mantine NumberInput emits "" (NOT null, NOT 0). `Number("")` and
    // `Number("   ")` are both 0, which would turn "$5,000 and up" into
    // "$0 - $5,000" -- the exact opposite set -- so blank means NO BOUND.
    if (value.trim() === "") {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

/**
 * The COMMITTED state of the merged row: a discriminated union, so the states
 * the UI can reach are exactly the states the question has answers for.
 *
 *  - `all` carries nothing, because All reveals nothing. There is no "All plus a
 *    hidden armed range" to contradict the row's own summary.
 *  - `none` carries no min/max at all, so "no previous sales between $1k and $5k"
 *    -- the self-contradiction the two-row design allowed -- is not merely
 *    guarded against, it cannot be constructed.
 *
 * THE $0 TRAP INVERTS HERE. EIS-1093 needed a $1 minimum as a GUARD because a
 * standalone "$0 and up" range swept in every practitioner the keystats service
 * zero-fills, i.e. everyone who never bought anything. Under the merged filter
 * the range is only ever ANDed with the Has segment's own `sales > 0`, so a $0
 * minimum can no longer admit a never-bought account. $1 stays the DEFAULT
 * minimum; it is NOT a clamp, and re-adding one would be a misread of EIS-1097.
 */
export type PreviousSalesState =
  | { mode: typeof PREVIOUS_SALES_ALL }
  | {
    mode: typeof PREVIOUS_SALES_HAS;
    windowMonths: PreviousSalesWindowMonths;
    min: number | null;
    max: number | null;
  }
  | { mode: typeof PREVIOUS_SALES_NONE; windowMonths: PreviousSalesWindowMonths };

/**
 * The values the EDITOR is holding, including the ones the current segment does
 * not show. AC2: switching All -> Has -> No -> Has must not silently discard the
 * range the user typed, and the union above deliberately has nowhere to keep it.
 *
 * This is INERT with respect to behaviour: buildMapFilterBody and isFilterActive
 * read `previousSales` ONLY. Nothing in here can put a key on the wire or light
 * the header badge, which is what makes it safe to carry alongside the union
 * instead of re-introducing the flat shape the union replaced.
 */
export interface PreviousSalesDraft {
  windowMonths: PreviousSalesWindowMonths;
  min: number | null;
  max: number | null;
}

/** "$1 to no limit, in the last 12 months" -- the ticket's stated default Has
 *  state. `max: null` is NO UPPER BOUND ("and up"), never $0. */
export const DEFAULT_PREVIOUS_SALES_DRAFT: PreviousSalesDraft = {
  windowMonths: DEFAULT_PREVIOUS_SALES_WINDOW_MONTHS,
  min: 1,
  max: null,
};

export const DEFAULT_PREVIOUS_SALES_STATE: PreviousSalesState = {
  mode: PREVIOUS_SALES_ALL,
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);

/**
 * Project (segment, draft) onto the union. The single place the union is built,
 * so the "None carries no range" rule is enforced once rather than at every
 * call site.
 */
export const commitPreviousSales = (
  mode: PreviousSalesSelection,
  draft: PreviousSalesDraft,
): PreviousSalesState => {
  if (mode === PREVIOUS_SALES_HAS) {
    return {
      mode: PREVIOUS_SALES_HAS,
      windowMonths: draft.windowMonths,
      min: draft.min,
      max: draft.max,
    };
  }
  if (mode === PREVIOUS_SALES_NONE) {
    return { mode: PREVIOUS_SALES_NONE, windowMonths: draft.windowMonths };
  }
  return { mode: PREVIOUS_SALES_ALL };
};

/**
 * Coerce ANY persisted `previousSales` value into the union.
 *
 * Accepts, in one function, every shape three shipped builds could have written:
 *   EIS-1088  ["has"] / ["none"] / ["has","none"] / []   (via previousSalesSelection)
 *   EIS-1091  "all" / "has" / "none"                      (via previousSalesSelection)
 *   EIS-1097  { mode, windowMonths, min, max }
 * plus a missing key, null, and outright junk -- all of which land on All, the
 * selection that removes no pins.
 *
 * `"min" in raw` rather than `??`: a user who deliberately CLEARED the minimum
 * persisted an explicit null, and resurrecting that to $1 on reload would
 * silently narrow their filter behind their back. Only an ABSENT key gets the
 * default.
 */
export const previousSalesState = (value: unknown): PreviousSalesState => {
  if (isPlainObject(value)) {
    const mode = previousSalesSelection(value.mode);
    if (mode === PREVIOUS_SALES_ALL) {
      return { mode: PREVIOUS_SALES_ALL };
    }
    const windowMonths = previousSalesWindowMonths(value.windowMonths);
    if (mode === PREVIOUS_SALES_NONE) {
      return { mode: PREVIOUS_SALES_NONE, windowMonths };
    }
    return {
      mode: PREVIOUS_SALES_HAS,
      windowMonths,
      min: "min" in value
        ? normalizeDollarBound(value.min)
        : DEFAULT_PREVIOUS_SALES_DRAFT.min,
      max: "max" in value
        ? normalizeDollarBound(value.max)
        : DEFAULT_PREVIOUS_SALES_DRAFT.max,
    };
  }
  // Legacy scalar / array / junk: no window and no range were ever recorded, so
  // both take the defaults (12 months, $1 and up) -- which is precisely what the
  // pre-EIS-1097 builds meant.
  return commitPreviousSales(previousSalesSelection(value), DEFAULT_PREVIOUS_SALES_DRAFT);
};

/** Coerce ANY persisted draft into a complete one. */
export const previousSalesDraftOf = (value: unknown): PreviousSalesDraft => {
  const raw = isPlainObject(value) ? value : {};
  return {
    windowMonths: previousSalesWindowMonths(raw.windowMonths),
    min: "min" in raw
      ? normalizeDollarBound(raw.min)
      : DEFAULT_PREVIOUS_SALES_DRAFT.min,
    max: "max" in raw
      ? normalizeDollarBound(raw.max)
      : DEFAULT_PREVIOUS_SALES_DRAFT.max,
  };
};

/**
 * AC11 MIGRATION: fold a persisted `map-filters` blob from ANY earlier build onto
 * (union, draft).
 *
 * The legacy pair being merged away:
 *   previousSales                                   -> the mode
 *   minRolling12mSales / maxRolling12mSales          -> the Has segment's range
 *   isRolling12mFilterActive                         -> DROPPED (see below)
 *
 * The range is carried forward as the DRAFT so nothing the user typed is lost,
 * but it is only ARMED -- i.e. only reaches the union, and therefore the wire --
 * when the migrated mode is `has`. A legacy blob holding "All" plus an armed
 * $1k-$5k range therefore migrates to an INACTIVE filter: All reveals nothing,
 * so an armed range under it would be exactly the contradiction this ticket
 * deletes. That direction is deliberately the safe one -- MORE pins, never
 * fewer, and never a white screen.
 *
 * `isRolling12mFilterActive` has no successor because the segment IS the arming:
 * `all` is off, `has`/`none` are on, and no third combination exists.
 */
export const migratePreviousSales = (blob: unknown): {
  previousSales: PreviousSalesState;
  previousSalesDraft: PreviousSalesDraft;
} => {
  const raw = isPlainObject(blob) ? blob : {};
  const persisted = raw.previousSales;

  // Already the merged shape: the union is authoritative, and the draft mirrors
  // it whenever Has is selected so the editor never shows values the wire is not
  // using.
  if (isPlainObject(persisted)) {
    const state = previousSalesState(persisted);
    const stored = previousSalesDraftOf(raw.previousSalesDraft);
    if (state.mode === PREVIOUS_SALES_HAS) {
      return {
        previousSales: state,
        previousSalesDraft: {
          windowMonths: state.windowMonths,
          min: state.min,
          max: state.max,
        },
      };
    }
    return {
      previousSales: state,
      previousSalesDraft: {
        windowMonths: state.mode === PREVIOUS_SALES_NONE
          ? state.windowMonths
          : stored.windowMonths,
        min: stored.min,
        max: stored.max,
      },
    };
  }

  const mode = previousSalesSelection(persisted);
  const draft: PreviousSalesDraft = {
    // No pre-EIS-1097 build recorded a window; every one of them meant 12.
    windowMonths: DEFAULT_PREVIOUS_SALES_WINDOW_MONTHS,
    min: "minRolling12mSales" in raw
      ? normalizeDollarBound(raw.minRolling12mSales)
      : DEFAULT_PREVIOUS_SALES_DRAFT.min,
    max: "maxRolling12mSales" in raw
      ? normalizeDollarBound(raw.maxRolling12mSales)
      : DEFAULT_PREVIOUS_SALES_DRAFT.max,
  };
  return { previousSales: commitPreviousSales(mode, draft), previousSalesDraft: draft };
};

const formatDollars = (value: number): string => `$${value.toLocaleString("en-US")}`;

/**
 * EIS-1097 round 3: THE RANGE THAT ASKS FOR ACCOUNTS THAT CANNOT EXIST.
 *
 * Round 2 stopped non-scalar junk from becoming a bound. It did nothing about a
 * perfectly well-formed NUMBER that makes the query self-contradictory, and the
 * user can type one straight into the NumberInputs (or carry one in from a
 * persisted blob):
 *
 *   max <= 0 under Has -- Has already means `sales_in_window > 0`, so this asks
 *     for `sales > 0 AND sales <= 0`. Nothing can satisfy it. `-0` counts: it
 *     serialises to `0`, and `=== 0` would miss it while `<= 0` catches it.
 *   min > max -- the empty interval, at any window.
 *
 * Both used to come back as a blank map with NO explanation anywhere, which
 * reads as an outage or a bug rather than as an answer.
 *
 * THIS IS NOT A GUARD AND MUST NEVER BECOME ONE. It returns COPY, not a
 * corrected value: the ticket bans clamping, and rewriting `max: 0` into "no
 * upper bound" would answer a different question than the row is displaying.
 * The question still goes to the server verbatim; the user is simply TOLD what
 * is going to come back.
 *
 * A NEGATIVE MINIMUM IS DELIBERATELY NOT HERE. `min: -500` under Has is a no-op
 * lower bound, not a contradiction -- every account with sales > 0 still
 * matches -- so warning about it would be EIS-1093's mistake (a warning that
 * tells the user something false) in new copy. Sub-zero minima go to the server
 * untouched.
 *
 * Returning the SENTENCE rather than a boolean keeps the predicate and the words
 * in one place, so a future edit cannot leave the UI warning about one condition
 * in the language of another.
 */
export const PREVIOUS_SALES_IMPOSSIBLE_MAX_MESSAGE =
  "No account can match this range. “Has previous sales” already means more than "
  + "$0, so a maximum of $0 or less rules out every account — the map will come "
  + "back empty.";

export const PREVIOUS_SALES_INVERTED_RANGE_MESSAGE =
  "No account can match this range: the minimum is above the maximum, so the "
  + "range is empty — the map will come back empty.";

export const previousSalesImpossibleRange = (
  min: number | null,
  max: number | null,
): string | null => {
  if (max !== null && max <= 0) {
    return PREVIOUS_SALES_IMPOSSIBLE_MAX_MESSAGE;
  }
  if (min !== null && max !== null && min > max) {
    return PREVIOUS_SALES_INVERTED_RANGE_MESSAGE;
  }
  return null;
};

/**
 * The marker the COLLAPSED row carries when the range it is describing cannot
 * match anything. The editor's warning is only visible with the drawer open, and
 * the state that empties the map survives the drawer being closed -- including
 * across a reload, from a persisted blob the user never opened at all.
 */
export const PREVIOUS_SALES_NO_MATCHES_SUMMARY = "no matches possible";

/** The range half of the collapsed-row summary. A blank max is "and up", NEVER
 *  "$0"; a blank min is "up to $X"; neither bound is "any amount". */
export const previousSalesRangeSummary = (
  min: number | null,
  max: number | null,
): string => {
  if (min !== null && max !== null) {
    return `${formatDollars(min)}–${formatDollars(max)}`;
  }
  if (min !== null) {
    return `${formatDollars(min)}+`;
  }
  if (max !== null) {
    return `up to ${formatDollars(max)}`;
  }
  return "any amount";
};

/**
 * The collapsed row's summary in the TWO pieces the row actually renders.
 *
 * `detail` is the describable part -- long, and therefore the part allowed to
 * ellipsize on a narrow panel. `noMatches` is the marker, which is NOT part of
 * that string: EIS-1097 round 4 found that appending it to a `truncate`d <Text>
 * made the ellipsis eat precisely the words that explain the empty map, i.e. CSS
 * removed the one guarantee that justified warning-instead-of-blocking. The row
 * renders the marker as its own non-shrinking element, so keep them apart here.
 */
export interface PreviousSalesSummaryParts {
  detail: string;
  noMatches: boolean;
  /**
   * ROUND 5: the two pieces joined the ONE canonical way, i.e. exactly what
   * `previousSalesSummary()` returns. The row renders the pieces (the marker
   * must stay outside the ellipsizing node), but it also has to be able to say
   * the whole sentence -- correctly ordered and separated -- to a screen reader.
   * Carrying the joined form HERE, rather than re-joining at the call site,
   * means there is still exactly one join in the codebase.
   */
  full: string;
}

/** The one and only place the two pieces are joined. */
const joinPreviousSalesSummary = (
  detail: string,
  noMatches: boolean,
): PreviousSalesSummaryParts => ({
  detail,
  noMatches,
  full: noMatches ? `${detail} · ${PREVIOUS_SALES_NO_MATCHES_SUMMARY}` : detail,
});

export const previousSalesSummaryParts = (
  value: unknown,
): PreviousSalesSummaryParts => {
  const state = previousSalesState(value);
  if (state.mode === PREVIOUS_SALES_ALL) {
    return joinPreviousSalesSummary(
      PREVIOUS_SALES_LABEL_MAP.get(PREVIOUS_SALES_ALL) as string,
      false,
    );
  }
  const windowLabel = PREVIOUS_SALES_WINDOW_LABEL_MAP.get(state.windowMonths)
    ?? `last ${state.windowMonths} months`;
  if (state.mode === PREVIOUS_SALES_NONE) {
    return joinPreviousSalesSummary(
      `${PREVIOUS_SALES_LABEL_MAP.get(PREVIOUS_SALES_NONE)} · ${windowLabel}`,
      false,
    );
  }
  // The row must not read as an ordinary active filter when the question it is
  // describing has no possible answer -- that is the whole reason the empty map
  // was mystifying. Marked, never rewritten: the numbers shown are still the
  // numbers sent.
  return joinPreviousSalesSummary(
    [
      PREVIOUS_SALES_LABEL_MAP.get(PREVIOUS_SALES_HAS),
      previousSalesRangeSummary(state.min, state.max),
      windowLabel,
    ].join(" · "),
    previousSalesImpossibleRange(state.min, state.max) !== null,
  );
};

/**
 * The collapsed row's one-line summary as a SINGLE string, e.g.
 *   All
 *   Has previous sales · $1+ · last 12 months
 *   No previous sales · last 6 months
 *   Has previous sales · $5,000–$100 · last 12 months · no matches possible
 *
 * Derived from the SAME union the wire body is derived from, so the row cannot
 * describe a filter the server was not asked for. This is the joined form of
 * previousSalesSummaryParts -- one source, so the string the tests pin and the
 * two elements the row renders can never drift apart.
 */
export const previousSalesSummary = (value: unknown): string =>
  previousSalesSummaryParts(value).full;

export const TYPE_TITLE_MAP = new Map<FilterType, string>([
  ["last-check-in", "Days Since Last Check-In"],
  ["status", "Status"],
  ["until-follow-up", "Days Untill Follow-Up"],
  ["specialty-type", "Specialty Type"],
  ["source", "Source"],
  // EIS-1097: the standalone "Rolling 12-Month Sales" entry is GONE -- its
  // dollar range now lives inside this row's Has segment.
  ["previous-sales", "Previous Sales"],
]);
